import { useState, type ReactNode } from "react";

import { describeError } from "../app/useResource";

/**
 * Briques partagées de l'écran d'administration. Outil d'exploitation : tables
 * Bulma, interrupteurs, formulaires — pas le soin du cockpit.
 */

/** Titre de section + zone d'actions optionnelle. */
export function PanelHeader({ title, hint, actions }: { title: string; hint?: string; actions?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
      <h2 style={{ font: "700 15px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>{title}</h2>
      {hint ? <span className="has-text-grey is-size-7">{hint}</span> : null}
      <span style={{ marginInlineStart: "auto" }}>{actions}</span>
    </div>
  );
}

/** Loading / erreur (avec réessai) / vide — sinon rend `children`. */
export function AsyncBlock({
  loading,
  error,
  empty,
  emptyLabel = "Rien à afficher.",
  onRetry,
  children,
}: {
  loading: boolean;
  error: unknown;
  empty?: boolean;
  emptyLabel?: string;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (error) {
    return (
      <div className="notification is-danger is-light" role="alert">
        <p>{describeError(error)}</p>
        {onRetry ? (
          <button type="button" className="button is-small mt-2" onClick={onRetry}>
            Réessayer
          </button>
        ) : null}
      </div>
    );
  }
  if (loading) return <p className="has-text-grey is-size-7">Chargement…</p>;
  if (empty) return <p className="has-text-grey is-size-7">{emptyLabel}</p>;
  return <>{children}</>;
}

/** Interrupteur accessible (checkbox stylée) avec état occupé. */
export function Toggle({
  checked,
  onChange,
  busy = false,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  busy?: boolean;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: disabled || busy ? "default" : "pointer" }}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled || busy}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="is-size-7">{busy ? "…" : label}</span>
    </label>
  );
}

/** Pastille d'état colorée (source / réseau / import). */
export function StatusTag({ tone, children }: { tone: "ok" | "warn" | "down" | "muted"; children: ReactNode }) {
  const bg =
    tone === "ok" ? "var(--sentinelle-ok)" : tone === "warn" ? "#b45309" : tone === "down" ? "var(--sentinelle-signal)" : "#9aa7b4";
  return (
    <span
      className="ovl"
      style={{ background: bg, color: "#fff", borderRadius: 2, padding: "1px 6px", letterSpacing: "0.08em" }}
    >
      {children}
    </span>
  );
}

/** Bloc `<pre>` repliable pour un `before`/`after` d'audit ou un digest. */
export function ExpandableJson({ value, label = "voir" }: { value: unknown; label?: string }) {
  const [open, setOpen] = useState(false);
  if (value === null || value === undefined) return <span className="has-text-grey">—</span>;
  return (
    <>
      <button
        type="button"
        className="button is-small is-white"
        style={{ height: 28, padding: "0 6px", fontSize: "0.7rem" }}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "masquer" : label}
      </button>
      {open ? (
        <pre
          style={{
            marginTop: 4,
            padding: 8,
            fontSize: "0.7rem",
            maxHeight: 220,
            overflow: "auto",
            background: "var(--bulma-scheme-main-bis)",
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : null}
    </>
  );
}

/** Horodatage lisible (date + heure locale) ; `—` si nul. */
export function formatStamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}
